<template>
  <div class="max-w-2xl mx-auto">
    <div class="text-center space-y-8 py-8">
     

      <!-- Status -->
      <div>
        <h2 class="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          {{ currentStatus.title }}
        </h2>
        <p class="text-slate-600 dark:text-slate-400">
          {{ currentStatus.description }}
        </p>
      </div>

      <!-- Progress Steps -->
      <div class="space-y-3">
        <div
          v-for="(step, i) in generationSteps"
          :key="i"
          class="flex items-center gap-3 p-3 border rounded-lg transition-all"
          :class="{
            'border-hilab/30 dark:border-hilab-dark/40 bg-hilab/10 dark:bg-hilab-dark/20 shadow-sm': currentStepIndex === i,
            'border-hilab/30 dark:border-hilab-dark/40 bg-hilab/10 dark:bg-hilab-dark/20': currentStepIndex > i,
            'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800': currentStepIndex < i
          }"
        >
          <div class="w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0"
               :class="{
                 'bg-hilab dark:bg-hilab-dark text-white': currentStepIndex === i,
                 'bg-hilab dark:bg-hilab-dark text-white': currentStepIndex > i,
                 'bg-slate-200 dark:bg-slate-700 text-slate-500': currentStepIndex < i
               }">
            <lucide-icon v-if="currentStepIndex > i" name="check" :size="20" />
            <lucide-icon v-else-if="currentStepIndex === i" name="refresh-cw" :size="20" class="animate-spin" />
            <span v-else>{{ i + 1 }}</span>
          </div>
          <div class="flex-1 text-left">
            <h4 class="font-semibold text-slate-900 dark:text-white text-sm">{{ step.label }}</h4>
            <p class="text-xs text-slate-600 dark:text-slate-400">{{ step.description }}</p>
          </div>
        </div>
      </div>

      <!-- Fun Fact -->
      <div class="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
        <p class="text-sm text-yellow-900 dark:text-yellow-100">
          💡 <strong>{{ $t('AI_ONBOARDING.GENERATION.FUN_FACT') || 'Bạn có biết?' }}</strong> {{ currentFunFact }}
        </p>
      </div>

      <!-- Error State -->
      <div v-if="error" class="space-y-4">
        <div class="flex flex-col items-center gap-3 p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <lucide-icon name="circle-alert" :size="48" class="text-red-500" />
          <h3 class="text-lg font-semibold text-red-900 dark:text-red-100">
            {{ $t('AI_ONBOARDING.GENERATION.ERROR_TITLE') || 'Oops! Có lỗi xảy ra' }}
          </h3>
          <p class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
        </div>
        <woot-button @click="retryGeneration">
          <lucide-icon name="refresh-cw" :size="16" class="mr-2" />
          {{ $t('GENERAL.RETRY') || 'Thử lại' }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import LucideIcon from 'dashboard/components/LucideIcon.vue';
import AiOnboardingAPI from 'dashboard/api/aiOnboarding';

export default {
  name: 'AiGenerationLoadingStep',
  
  components: {
    LucideIcon,
  },

  emits: ['next', 'error'],

  data() {
    return {
      currentStepIndex: 0,
      currentFunFactIndex: 0,
      error: null,
      funFactInterval: null,
    };
  },

  computed: {
    ...mapState('aiOnboarding', {
      providerSetup: state => state.providerSetup,
      botProfile: state => state.botProfile,
      conversationalAnswers: state => state.conversationalAnswers,
      botModes: state => state.botModes,
      questionBank: state => state.questionBank,
    }),

    generationSteps() {
      return [
        {
          label: this.$t('AI_ONBOARDING.GENERATION.STEPS.ANALYZING.LABEL') || 'Phân tích thông tin',
          description: this.$t('AI_ONBOARDING.GENERATION.STEPS.ANALYZING.DESC') || 'AI đang đọc và hiểu về doanh nghiệp',
        },
        {
          label: this.$t('AI_ONBOARDING.GENERATION.STEPS.KNOWLEDGE.LABEL') || 'Tạo kiến thức nền',
          description: this.$t('AI_ONBOARDING.GENERATION.STEPS.KNOWLEDGE.DESC') || 'Xây dựng cơ sở dữ liệu FAQ',
        },
        {
          label: this.$t('AI_ONBOARDING.GENERATION.STEPS.TUNING.LABEL') || 'Tối ưu hóa câu trả lời',
          description: this.$t('AI_ONBOARDING.GENERATION.STEPS.TUNING.DESC') || 'Tinh chỉnh giọng điệu',
        },
        {
          label: this.$t('AI_ONBOARDING.GENERATION.STEPS.FINALIZING.LABEL') || 'Chuẩn bị chatbot',
          description: this.$t('AI_ONBOARDING.GENERATION.STEPS.FINALIZING.DESC') || 'Hoàn tất và sẵn sàng test',
        },
      ];
    },

    statusMessages() {
      return [
        {
          title: this.$t('AI_ONBOARDING.GENERATION.STATUS.ANALYZING_TITLE') || '🔍 Đang phân tích...',
          description: this.$t('AI_ONBOARDING.GENERATION.STATUS.ANALYZING_DESC') || 'AI đang đọc thông tin bạn cung cấp',
        },
        {
          title: this.$t('AI_ONBOARDING.GENERATION.STATUS.KNOWLEDGE_TITLE') || '🧠 Đang tạo kiến thức...',
          description: this.$t('AI_ONBOARDING.GENERATION.STATUS.KNOWLEDGE_DESC') || 'Xây dựng cơ sở dữ liệu FAQ',
        },
        {
          title: this.$t('AI_ONBOARDING.GENERATION.STATUS.TUNING_TITLE') || '✨ Đang tối ưu hóa...',
          description: this.$t('AI_ONBOARDING.GENERATION.STATUS.TUNING_DESC') || 'Tinh chỉnh giọng điệu',
        },
        {
          title: this.$t('AI_ONBOARDING.GENERATION.STATUS.FINALIZING_TITLE') || '🚀 Sắp hoàn thành...',
          description: this.$t('AI_ONBOARDING.GENERATION.STATUS.FINALIZING_DESC') || 'Chuẩn bị chatbot để test',
        },
      ];
    },

    currentStatus() {
      return this.statusMessages[this.currentStepIndex] || this.statusMessages[0];
    },

    currentFunFact() {
      return this.funFacts[this.currentFunFactIndex] || this.funFacts[0];
    },

    funFacts() {
      const translated = this.$t('AI_ONBOARDING.GENERATION.FUN_FACTS');
      if (Array.isArray(translated) && translated.length) {
        return translated;
      }
      return [
        'AI có thể trả lời hàng nghìn câu hỏi cùng lúc mà không cần nghỉ',
        'Chatbot sẽ học hỏi và cải thiện qua từng cuộc hội thoại',
        'Khách hàng thích nhận phản hồi trong vòng 2 phút',
        'AI có thể giảm tới 70% khối lượng công việc hỗ trợ',
        'Chatbot làm việc 24/7 mà không cần lương đêm',
        'Chatbot tốt có thể tăng conversion rate lên 30%',
      ];
    },
  },

  mounted() {
    this.startGeneration();
    this.startFunFactRotation();
  },

  beforeUnmount() {
    this.stopFunFactRotation();
  },

  methods: {
    ...mapActions('aiOnboarding', ['setGeneratedData']),

    async startGeneration() {
      this.error = null;
      this.currentStepIndex = 0;

      try {
        await this.simulateStep(0, 2000);
        
        this.currentStepIndex = 1;
        const response = await this.callGenerateAPI();
        await this.simulateStep(1, 1500);

        this.currentStepIndex = 2;
        await this.simulateStep(2, 2000);

        this.currentStepIndex = 3;
        await this.simulateStep(3, 1500);

        await this.setGeneratedData(response.data);
        this.$emit('next');
      } catch (error) {
        console.error('Generation failed:', error);
        this.error = error.response?.data?.message || this.$t('AI_ONBOARDING.GENERATION.ERROR') || 'Không thể tạo dữ liệu';
        this.$emit('error', this.error);
      }
    },

    async callGenerateAPI() {
      return await AiOnboardingAPI.generate({
        provider_setup: this.providerSetup,
        bot_profile: this.botProfile,
        conversational_answers: {
          ...this.conversationalAnswers,
          question_bank: this.questionBank,
        },
        bot_modes: this.botModes,
      });
    },

    async simulateStep(stepIndex, duration) {
      return new Promise(resolve => {
        setTimeout(() => {
          this.currentStepIndex = stepIndex;
          resolve();
        }, duration);
      });
    },

    startFunFactRotation() {
      this.funFactInterval = setInterval(() => {
        this.currentFunFactIndex = (this.currentFunFactIndex + 1) % this.funFacts.length;
      }, 5000);
    },

    stopFunFactRotation() {
      if (this.funFactInterval) {
        clearInterval(this.funFactInterval);
      }
    },

    retryGeneration() {
      this.startGeneration();
    },
  },
};
</script>
